import { useNostr } from '@nostrify/react';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import type { NostrEvent, NostrFilter } from '@nostrify/nostrify';

import { ownerPubkey } from '@/lib/owner';

const PAGE_SIZE = 10;

/**
 * Infinite-scrolling feed of the site owner's Nostr activity:
 * kind-1 notes (posts & replies) and kind-6 reposts, newest first.
 */
export function useOwnerFeed() {
  const { nostr } = useNostr();

  return useInfiniteQuery({
    queryKey: ['owner-feed', ownerPubkey],
    queryFn: async ({ pageParam, signal }) => {
      const filter: NostrFilter = {
        kinds: [1, 6],
        authors: [ownerPubkey],
        limit: PAGE_SIZE,
      };
      if (pageParam) filter.until = pageParam;

      const events = await nostr.query([filter], {
        signal: AbortSignal.any([signal, AbortSignal.timeout(8000)]),
      });

      return events.sort((a, b) => b.created_at - a.created_at);
    },
    initialPageParam: undefined as number | undefined,
    getNextPageParam: (lastPage: NostrEvent[]) =>
      lastPage.length < PAGE_SIZE ? undefined : lastPage[lastPage.length - 1].created_at - 1,
    enabled: Boolean(ownerPubkey),
  });
}

/**
 * Polls the relays every 30s for posts the owner published after `since`.
 * Used to show a "N new posts" pill at the top of the feed — so returning
 * visitors (especially logged-in ones) see fresh activity.
 */
export function useNewOwnerPosts(since: number | null) {
  const { nostr } = useNostr();

  return useQuery({
    queryKey: ['owner-feed-new', ownerPubkey, since],
    queryFn: async ({ signal }) => {
      if (!since) return [];
      return nostr.query(
        [{ kinds: [1, 6], authors: [ownerPubkey], since: since + 1, limit: 30 }],
        { signal: AbortSignal.any([signal, AbortSignal.timeout(5000)]) },
      );
    },
    enabled: Boolean(ownerPubkey && since),
    refetchInterval: 30_000,
  });
}
